var _jsxFileName = "/opt/atlassian/pipelines/agent/build/src/hooks/useAuth.tsx";
import React, { useCallback, useState } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { RichText } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { getDifferenceInMinutesFromNow } from 'Utilities/dateHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout, persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { signIn } from 'Services/auth';
import useApiError from './useApiError';
const useAuth = onDisplayLockoutMessage => {
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');
  const [lockoutSubmessage, setlockoutSubmessage] = useState('');
  const {
    showApiErrorModal
  } = useApiError();
  const redirectOnError = useCallback((redirectUrl, statusCode) => {
    if (redirectUrl) {
      const separator = redirectUrl.includes('?') ? '&' : '?';
      window.location.replace(`${redirectUrl}${separator}errorcode=${statusCode}`);
    }
  }, []);
  const showLockoutMessage = useCallback(attemptsResetAt => {
    const {
      time,
      difference
    } = getDifferenceInMinutesFromNow(attemptsResetAt);
    if (difference < 0) {
      return;
    }
    setIsLocked(true);
    setLockoutMessage(i18n.t('Login.modals.signInErrors.accountLocked'));
    setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', {
      time
    }));
    const lockoutInterval = setInterval(() => {
      const {
        time,
        difference
      } = getDifferenceInMinutesFromNow(attemptsResetAt);
      if (difference < 0) {
        clearInterval(lockoutInterval);
        setLockoutMessage('');
        setlockoutSubmessage('');
        setIsLocked(false);
      } else {
        setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', {
          time
        }));
      }
    }, 1000);
    onDisplayLockoutMessage && onDisplayLockoutMessage();
  }, [onDisplayLockoutMessage]);
  const handleLockoutError = useCallback(error => {
    const attemptsResetAt = Number(error.response?.headers['x-attempts-reset-at']);
    showLockoutMessage(attemptsResetAt);
  }, [showLockoutMessage]);
  const showLoginFailure = useCallback((attemptsRemaining = -1, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose) => {
    const modalProps = {
      details: /*#__PURE__*/React.createElement(Stack, {
        gap: 4,
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 89,
          columnNumber: 11
        }
      }, /*#__PURE__*/React.createElement(Text, {
        color: "$text.100",
        type: "$bodyBase.default",
        nativeID: "Login.modals.signInErrors.loginFailure",
        testID: "Login.modals.signInErrors.loginFailure",
        marginBottom: "$1",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 90,
          columnNumber: 13
        }
      }, i18n.t('Login.modals.signInErrors.loginFailure')), attemptsRemaining >= 0 ? /*#__PURE__*/React.createElement(Stack, {
        display: "inline",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 100,
          columnNumber: 15
        }
      }, /*#__PURE__*/React.createElement(Text, {
        color: "$text.100",
        type: "$bodyBase.default",
        nativeID: "Login.modals.signInErrors.attemptsRemaining",
        testID: "Login.modals.signInErrors.attemptsRemaining",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 101,
          columnNumber: 17
        }
      }, i18n.t(`${attemptsRemaining === 1 ? 'Login.modals.signInErrors.attemptsRemaining' : 'Login.modals.signInErrors.attemptsRemaining_other'}`, {
        count: attemptsRemaining
      })), attemptsResetAfter ? /*#__PURE__*/React.createElement(Text, {
        color: "$text.100",
        type: "$bodyBase.medium",
        nativeID: "Login.modals.signInErrors.accountWillBeLocked",
        testID: "Login.modals.signInErrors.accountWillBeLocked",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 119,
          columnNumber: 19
        }
      }, i18n.t('Login.modals.signInErrors.accountWillBeLocked', {
        time: attemptsResetAfter,
        duration: attemptsResetAfter
      })) : null) : null),
      testID: 'Auth'
    };
    modalProps.onClose = () => {
      onClose && onClose();
    };
    if (attemptsResetAfter && attemptsResetAt) {
      modalProps.onClose = () => {
        showLockoutMessage(attemptsResetAt);
        onCloseAfterLockout && onCloseAfterLockout();
      };
    }
    store.dispatch({
      type: SHOW_MODAL,
      modalProps
    });
  }, [showLockoutMessage]);
  const handleLoginError = useCallback((error, onCloseAfterLockout, onClose) => {
    if (error.response?.data.details === 'Authorization.isRequired') {
      logout();
      return;
    }
    if (error.response && error.response.headers['x-attempts-remaining']) {
      const attemptsRemaining = Number(error.response.headers['x-attempts-remaining']);
      const attemptsResetAt = error.response.headers['x-attempts-reset-at'] ? Number(error.response.headers['x-attempts-reset-at']) : 0;
      const attemptsResetAfter = error.response.headers['x-attempts-reset-after'] ? Number(error.response.headers['x-attempts-reset-after']) / 60 : 0;
      showLoginFailure(attemptsRemaining, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose);
      return;
    }
    showLoginFailure();
  }, [showLoginFailure]);
  const handleCountryRegionError = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: i18n.t('Login.modals.signInErrors.regionDoesNotMatch'),
        testID: 'RegionError'
      }
    });
  }, []);
  const handleForgotPasswordDone = useCallback(() => {
    mediator.publish('router:navigate', '/login');
  }, []);
  const handleResetPasswordDone = useCallback(() => {
    mediator.publish('router:navigate', '/login');
  }, []);
  const showHcpError = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: 4,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 211,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement(RichText, {
          text: i18n.t('Global.modals.blockHCPUser.body', {
            appName: i18n.t('Global.appNames.libreView')
          }),
          testID: "Global.modals.blockHCPUser.body",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 212,
            columnNumber: 13
          }
        })),
        testID: 'Auth'
      }
    });
  }, []);
  const showTokenExpirationError = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: 4,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 230,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement(RichText, {
          testID: "Global.modals.linkNotValid.title",
          text: i18n.t('Global.modals.linkNotValid.title'),
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 231,
            columnNumber: 13
          }
        }), /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          nativeID: "Global.modals.linkNotValid.body",
          testID: "Global.modals.linkNotValid.body",
          marginTop: "$1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 235,
            columnNumber: 13
          }
        }, i18n.t('Global.modals.linkNotValid.body'))),
        testID: 'Auth'
      }
    });
  }, []);
  const showForgotPasswordBlockedMessage = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: 4,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 256,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          nativeID: "ForgotPassword.modals.forgotErrors.youReached",
          testID: "ForgotPassword.modals.forgotErrors.youReached",
          marginBottom: "$1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 257,
            columnNumber: 13
          }
        }, `${i18n.t('ForgotPassword.modals.forgotErrors.youReached')}`), /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          nativeID: "ForgotPassword.modals.forgotErrors.consider",
          testID: "ForgotPassword.modals.forgotErrors.consider",
          marginBottom: "$1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 264,
            columnNumber: 13
          }
        }, `${i18n.t('ForgotPassword.modals.forgotErrors.consider')}`)),
        testID: 'Auth'
      }
    });
  }, []);
  const showForgotPasswordConfirmatioMessage = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: 4,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 283,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          nativeID: "ForgotPassword.modals.forgotErrors.sentInstructions",
          testID: "ForgotPassword.modals.forgotErrors.sentInstructions",
          marginBottom: "$1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 284,
            columnNumber: 13
          }
        }, i18n.t('ForgotPassword.modals.forgotErrors.sentInstructions')), /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          nativeID: "ForgotPassword.modals.forgotErrors.notSeeingEmail",
          testID: "ForgotPassword.modals.forgotErrors.notSeeingEmail",
          marginBottom: "$1",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 293,
            columnNumber: 13
          }
        }, i18n.t('ForgotPassword.modals.forgotErrors.notSeeingEmail'))),
        testID: 'Auth'
      }
    });
  }, []);
  const handleUnexpectedError = useCallback(() => {
    showApiErrorModal();
  }, [showApiErrorModal]);
  const showCountryRegionError = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: i18n.t('Login.modals.signInErrors.regionDoesNotMatch'),
        testID: 'Auth'
      }
    });
  }, []);
  const navigateToPhoneVerification = useCallback((task, onSuccessfulVerify) => {
    mediator.publish('router:navigate', {
      url: '/verify-number',
      data: {
        onSuccessfulVerify,
        taskID: task.id,
        mobileNumber: task.verificationTarget
      }
    });
  }, []);

  /**
   * Authenticates a user and manages auth session
   *
   * @param {string} email - The user's email.
   * @param {string} password - The user's password.
   * @param { 'login' | 'register' } authOrigin - The origin of the authentication request, used for redirection after consent updates.
   * - 'login': Redirects the user after auth.
   * - 'register': Redirects the user to the sign in page.
   */
  const authenticateUser = useCallback(async (email, password, authOrigin = 'login') => {
    const {
      cid,
      app
    } = store.getState().env;
    const {
      accessToken,
      accountId,
      expires,
      verificationTasks,
      patient
    } = await signIn(email, password, app);
    persistAuthSession({
      accessToken,
      accountId,
      expires,
      cid,
      app,
      country: patient.country,
      authOrigin
    });
    return {
      verificationTasks,
      accountId,
      accessToken,
      patient
    };
  }, []);
  const hasPendingConsents = useCallback(patient => {
    const {
      consents
    } = patient;
    if (!consents) {
      return false;
    }
    const hasOutdatedHealthConsent = Object.hasOwn(consents, 'hipaa') && !consents.hipaa?.at || Object.hasOwn(consents, 'crm') && !consents.crm?.at;
    const hasOutdatedRwe = Object.hasOwn(consents, 'realWorldEvidence') && !consents.realWorldEvidence?.at;
    if (hasOutdatedHealthConsent) {
      mediator.publish('router:navigate', '/hipaa');
      return true;
    }
    if (hasOutdatedRwe) {
      mediator.publish('router:navigate', '/rwe');
      return true;
    }
    return false;
  }, []);
  return {
    handleLoginError,
    handleLockoutError,
    handleCountryRegionError,
    handleForgotPasswordDone,
    handleResetPasswordDone,
    handleUnexpectedError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    showTokenExpirationError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showCountryRegionError,
    redirectOnError,
    navigateToPhoneVerification,
    authenticateUser,
    hasPendingConsents
  };
};
export default useAuth;